import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import {
	Toast,
	Button,
	CardLayout,
	Input,
	LoadingOverlay,
	Text,
	spacing,
	useInput,
} from '@pletly/ui';

import { useGetOrganisationInfoQuery } from './components/__generated__/organisationInfo.generated';
import { useUpdateOrganisationMutation } from './components/__generated__/updateOrganistaion.mutation.generated';
import { RootStackScreenProps } from '../../navigation/types';
import { withTranslation } from '../../locales/hoc';

type Props = RootStackScreenProps<'EditOrganisation'>;

const EditOrganisationPage = ({
	route: {
		params: { uuid },
	},
}: Props) => {
	const { goBack, navigate } = useNavigation();
	const { t } = useTranslation();
	const nameInput = useInput();
	const { loading: orgLoading } = useGetOrganisationInfoQuery({
		variables: { uuid },
		onCompleted(data) {
			nameInput.onChangeText(data.organisation.name);
		},
	});

	const [updateMutation] = useUpdateOrganisationMutation();

	const handleSavePress = useCallback(async () => {
		try {
			await updateMutation({
				variables: {
					data: {
						uuid,
						name: nameInput.value,
					},
				},
			});

			Toast.show({
				text1: t('toasts.success.organization-updated'),
				type: 'success',
			});
			navigate('Home', { uuid });
		} catch (error) {
			console.log(error);
			Toast.show({
				text1: t('toasts.error.something-wrong'),
				type: 'error',
			});
		}
	}, [uuid, nameInput.value, updateMutation, navigate, t]);

	return (
		<CardLayout title={t('edit-organization')}>
			<LoadingOverlay show={orgLoading} />
			<Input
				label={t('inputs.organization-name')}
				placeholder={t('inputs.organization-name')}
				{...nameInput}
			/>

			<View style={styles.section}>
				<Text category="h5">{t('terms-of-use.title')}</Text>
				<Button
					color="primary"
					variant="outline"
					size="small"
					onPress={() => navigate('AllTermsOfUse', { uuid })}
				>
					{t('terms-of-use.manage-terms-of-use')}
				</Button>
			</View>

			<View style={styles.buttons}>
				<Button variant="outline" color="primary" onPress={goBack} style={styles.button}>
					{t('common.cancel')}
				</Button>
				<Button variant="filled" color="primary" onPress={handleSavePress} style={styles.button}>
					{t('common.save')}
				</Button>
			</View>
		</CardLayout>
	);
};

const styles = StyleSheet.create({
	button: {
		flex: 1,
	},
	buttons: {
		flexDirection: 'row',
		gap: 16,
		marginTop: spacing(4),
	},
	section: {
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: spacing(2),
		marginTop: spacing(2),
	},
});

export const EditOrganisation = withTranslation(EditOrganisationPage);
